import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  ArrowLeft,
  Plus,
  User,
  Users,
  Calendar,
  MapPin,
  Briefcase,
  Church,
  Loader2,
  Trash2,
  Heart,
  Baby,
  Pencil,
  FileText,
  Image as ImageIcon,
  File,
  FileSpreadsheet,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { PlaceSearch } from '@/components/PlaceSearch';
import type {
  Person,
  PersonWithCounts,
  PersonSummary,
  Relation,
  RelationType,
  CreateRelationInput,
  Gender,
  Place,
  Document,
} from '@antree/shared';
import { DOCUMENT_TYPE_LABELS } from '@antree/shared';

// Convert YYYY-MM-DD to DD.MM.YYYY for display
function formatDateDE(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${day}.${month}.${year}`;
}

interface PersonResponse {
  person: Person & {
    relations?: Array<{
      id: string;
      type: RelationType;
      person: PersonSummary;
      role: 'subject' | 'relative';
    }>;
  };
}

interface PersonsResponse {
  persons: PersonWithCounts[];
}

interface RelationResponse {
  relation: Relation;
}

interface DocumentsResponse {
  documents: Document[];
}

export function PersonDetail() {
  const { id: projectId, personId } = useParams<{ id: string; personId: string }>();
  const [person, setPerson] = useState<PersonResponse['person'] | null>(null);
  const [allPersons, setAllPersons] = useState<PersonWithCounts[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Edit person state
  const [editPersonOpen, setEditPersonOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editMiddleName, setEditMiddleName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editBirthName, setEditBirthName] = useState('');
  const [editGender, setEditGender] = useState<Gender | ''>('');
  const [editBirthDate, setEditBirthDate] = useState('');
  const [editDeathDate, setEditDeathDate] = useState('');
  const [editBirthPlace, setEditBirthPlace] = useState<Place | null>(null);
  const [editDeathPlace, setEditDeathPlace] = useState<Place | null>(null);
  const [editBurialPlace, setEditBurialPlace] = useState<Place | null>(null);
  const [editOccupation, setEditOccupation] = useState('');
  const [editReligion, setEditReligion] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editPersonError, setEditPersonError] = useState<string | null>(null);

  // Relation form state
  const [selectedPersonId, setSelectedPersonId] = useState('');
  const [relationType, setRelationType] = useState<RelationType | ''>('');

  useEffect(() => {
    if (projectId && personId) {
      loadPerson();
      loadAllPersons();
      loadDocuments();
    }
  }, [projectId, personId]);

  async function loadPerson() {
    setLoading(true);
    const response = await api.get<PersonResponse>(`/projects/${projectId}/persons/${personId}`);
    if (response.success && response.data) {
      setPerson(response.data.person);
    }
    setLoading(false);
  }

  async function loadAllPersons() {
    const response = await api.get<PersonsResponse>(`/projects/${projectId}/persons`);
    if (response.success && response.data) {
      setAllPersons(response.data.persons);
    }
  }

  async function loadDocuments() {
    const response = await api.get<DocumentsResponse>(`/projects/${projectId}/documents`);
    if (response.success && response.data) {
      // Filter documents that have this person tagged
      const taggedDocs = response.data.documents.filter(doc =>
        doc.tags.some(tag => tag.personId === personId)
      );
      setDocuments(taggedDocs);
    }
  }

  function resetForm() {
    setSelectedPersonId('');
    setRelationType('');
    setError(null);
  }

  function openEditPerson() {
    if (person) {
      setEditFirstName(person.firstName || '');
      setEditMiddleName(person.middleName || '');
      setEditLastName(person.lastName || '');
      setEditBirthName(person.birthName || '');
      setEditGender(person.gender || '');
      setEditBirthDate(person.birthDate || '');
      setEditDeathDate(person.deathDate || '');
      setEditBirthPlace(person.birthPlace || null);
      setEditDeathPlace(person.deathPlace || null);
      setEditBurialPlace(person.burialPlace || null);
      setEditOccupation(person.occupation || '');
      setEditReligion(person.religion || '');
      setEditNotes(person.notes || '');
      setEditPersonError(null);
      setEditPersonOpen(true);
    }
  }

  async function handleUpdatePerson(e: React.FormEvent) {
    e.preventDefault();
    if (!editFirstName.trim() && !editLastName.trim()) {
      setEditPersonError('Bitte mindestens Vor- oder Nachname eingeben');
      return;
    }

    setEditingPerson(true);
    setEditPersonError(null);

    const data: Record<string, string | null> = {
      firstName: editFirstName.trim() || null,
      middleName: editMiddleName.trim() || null,
      lastName: editLastName.trim() || null,
      birthName: editBirthName.trim() || null,
      gender: editGender || null,
      birthDate: editBirthDate.trim() || null,
      deathDate: editDeathDate.trim() || null,
      birthPlaceId: editBirthPlace?.id || null,
      deathPlaceId: editDeathPlace?.id || null,
      burialPlaceId: editBurialPlace?.id || null,
      occupation: editOccupation.trim() || null,
      religion: editReligion.trim() || null,
      notes: editNotes.trim() || null,
    };

    const response = await api.put<PersonResponse>(`/projects/${projectId}/persons/${personId}`, data);

    if (response.success && response.data) {
      setPerson({
        ...person!,
        ...response.data.person,
        relations: person?.relations,
      });
      setEditPersonOpen(false);
    } else {
      setEditPersonError(response.error?.message || 'Fehler beim Speichern');
    }
    setEditingPerson(false);
  }

  async function handleCreateRelation(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedPersonId || !relationType) {
      setError('Bitte Person und Beziehungstyp auswahlen');
      return;
    }

    setCreating(true);
    setError(null);

    const data: CreateRelationInput = {
      subjectId: personId!,
      relativeId: selectedPersonId,
      type: relationType,
    };

    const response = await api.post<RelationResponse>(`/projects/${projectId}/relations`, data);

    if (response.success) {
      // Reload person to get updated relations
      await loadPerson();
      setDialogOpen(false);
      resetForm();
    } else {
      setError(response.error?.message || 'Fehler beim Erstellen der Beziehung');
    }
    setCreating(false);
  }

  async function handleDeleteRelation(relationId: string) {
    setDeleting(relationId);
    const response = await api.delete(`/projects/${projectId}/relations/${relationId}`);
    if (response.success) {
      await loadPerson();
    }
    setDeleting(null);
  }

  function formatName(p: PersonSummary | Person | null): string {
    if (!p) return 'Unbekannt';
    const parts = [];
    if (p.firstName) parts.push(p.firstName);
    if ('middleName' in p && p.middleName) parts.push(p.middleName);
    if (p.lastName) parts.push(p.lastName);
    if (parts.length === 0) return 'Unbekannt';
    return parts.join(' ');
  }

  function getGenderIcon(gender: Gender | null) {
    switch (gender) {
      case 'MALE':
        return <User className="h-5 w-5 text-blue-500" />;
      case 'FEMALE':
        return <User className="h-5 w-5 text-pink-500" />;
      default:
        return <User className="h-5 w-5 text-muted-foreground" />;
    }
  }

  // Group relations by type
  type PersonRelationItem = NonNullable<typeof person>['relations'] extends (infer T)[] | undefined ? T : never;
  type GroupedRelations = Record<RelationType, PersonRelationItem[]>;

  function groupRelations(): GroupedRelations {
    const grouped: GroupedRelations = {
      PARENT: [],
      CHILD: [],
      SPOUSE: [],
      PARTNER: [],
      SIBLING: [],
    };
    if (!person?.relations) return grouped;
    for (const rel of person.relations) {
      grouped[rel.type].push(rel);
    }
    return grouped;
  }

  // Get persons that can be related (exclude self and already related)
  function getAvailablePersons(): PersonWithCounts[] {
    const relatedIds = new Set(person?.relations?.map((r) => r.person.id) || []);
    return allPersons.filter((p) => p.id !== personId && !relatedIds.has(p.id));
  }

  if (loading) {
    return (
      <div className="container py-8">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="container py-8">
        <p>Person nicht gefunden.</p>
        <Link to={`/projects/${projectId}`}>
          <Button variant="link" className="px-0 hover:text-bronze-500">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück zum Projekt
          </Button>
        </Link>
      </div>
    );
  }

  const groupedRelations = groupRelations();
  const availablePersons = getAvailablePersons();

  return (
    <div className="container py-8">
      <div className="flex flex-col gap-6">
        {/* Back link */}
        <Link to={`/projects/${projectId}`}>
          <Button variant="ghost" className="px-0 hover:bg-transparent hover:text-bronze-500">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück zum Projekt
          </Button>
        </Link>

        {/* Person Header */}
        <div className="flex items-start gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            {getGenderIcon(person.gender)}
          </div>
          <div className="flex-1">
            <div className="flex items-start gap-2">
              <h1 className="text-3xl font-bold tracking-tight">{formatName(person)}</h1>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground"
                onClick={openEditPerson}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
            {person.birthName && person.birthName !== person.lastName && (
              <p className="text-muted-foreground">geb. {person.birthName}</p>
            )}
            {(person.birthDate || person.deathDate) && (
              <p className="text-muted-foreground">
                {formatDateDE(person.birthDate) || '?'} – {formatDateDE(person.deathDate) || ''}
              </p>
            )}
          </div>
        </div>

        {/* Edit Person Dialog */}
        <Dialog open={editPersonOpen} onOpenChange={setEditPersonOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-[500px] max-h-[90vh] flex flex-col p-0">
            <DialogHeader className="px-4 pt-4 sm:px-6 sm:pt-6">
              <DialogTitle>Person bearbeiten</DialogTitle>
              <DialogDescription>
                Bearbeite die Daten dieser Person.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdatePerson} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 space-y-4">
                {/* Namen - auf Mobil untereinander */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="editFirstName">Vorname</Label>
                    <Input
                      id="editFirstName"
                      value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                      placeholder="Max"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="editMiddleName">Zweiter Vorname</Label>
                    <Input
                      id="editMiddleName"
                      value={editMiddleName}
                      onChange={(e) => setEditMiddleName(e.target.value)}
                      placeholder="Heinrich"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="editLastName">Nachname</Label>
                    <Input
                      id="editLastName"
                      value={editLastName}
                      onChange={(e) => setEditLastName(e.target.value)}
                      placeholder="Mustermann"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="editBirthName">Geburtsname</Label>
                    <Input
                      id="editBirthName"
                      value={editBirthName}
                      onChange={(e) => setEditBirthName(e.target.value)}
                      placeholder="Falls abweichend"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="editGender">Geschlecht</Label>
                    <Select value={editGender} onValueChange={(v) => setEditGender(v as Gender)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MALE">Männlich</SelectItem>
                        <SelectItem value="FEMALE">Weiblich</SelectItem>
                        <SelectItem value="OTHER">Divers</SelectItem>
                        <SelectItem value="UNKNOWN">Unbekannt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="editBirthDate">Geburtsdatum</Label>
                    <Input
                      id="editBirthDate"
                      type="date"
                      value={editBirthDate}
                      onChange={(e) => setEditBirthDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="editDeathDate">Sterbedatum</Label>
                    <Input
                      id="editDeathDate"
                      type="date"
                      value={editDeathDate}
                      onChange={(e) => setEditDeathDate(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Geburtsort</Label>
                  <PlaceSearch
                    projectId={projectId!}
                    value={editBirthPlace}
                    onChange={setEditBirthPlace}
                    placeholder="Geburtsort suchen..."
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Sterbeort</Label>
                  <PlaceSearch
                    projectId={projectId!}
                    value={editDeathPlace}
                    onChange={setEditDeathPlace}
                    placeholder="Sterbeort suchen..."
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>Begraben in</Label>
                  <PlaceSearch
                    projectId={projectId!}
                    value={editBurialPlace}
                    onChange={setEditBurialPlace}
                    placeholder="Begräbnisort suchen..."
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="editOccupation">Beruf</Label>
                    <Input
                      id="editOccupation"
                      value={editOccupation}
                      onChange={(e) => setEditOccupation(e.target.value)}
                      placeholder="z.B. Schreiner"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="editReligion">Konfession</Label>
                    <Input
                      id="editReligion"
                      value={editReligion}
                      onChange={(e) => setEditReligion(e.target.value)}
                      placeholder="z.B. Katholisch"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="editNotes">Notizen</Label>
                  <Textarea
                    id="editNotes"
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Zusätzliche Informationen..."
                    rows={2}
                  />
                </div>

                {editPersonError && <p className="text-sm text-red-500">{editPersonError}</p>}
              </div>

              {/* Fixer Footer mit Buttons */}
              <div className="flex justify-end gap-2 p-4 sm:px-6 border-t bg-background">
                <Button type="button" variant="outline" onClick={() => setEditPersonOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={editingPerson}>
                  {editingPerson && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Person Details */}
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {person.gender && (
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>
                    {person.gender === 'MALE' && 'Mannlich'}
                    {person.gender === 'FEMALE' && 'Weiblich'}
                    {person.gender === 'OTHER' && 'Divers'}
                    {person.gender === 'UNKNOWN' && 'Unbekannt'}
                  </span>
                </div>
              )}
              {person.birthDate && (
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>Geboren: {formatDateDE(person.birthDate)}</span>
                </div>
              )}
              {person.birthPlace && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>Geburtsort: {person.birthPlace.name}</span>
                </div>
              )}
              {person.deathDate && (
                <div className="flex items-center gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>Gestorben: {formatDateDE(person.deathDate)}</span>
                </div>
              )}
              {person.deathPlace && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>Sterbeort: {person.deathPlace.name}</span>
                </div>
              )}
              {person.burialPlace && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>Begraben in: {person.burialPlace.name}</span>
                </div>
              )}
              {person.occupation && (
                <div className="flex items-center gap-3">
                  <Briefcase className="h-4 w-4 text-muted-foreground" />
                  <span>{person.occupation}</span>
                </div>
              )}
              {person.religion && (
                <div className="flex items-center gap-3">
                  <Church className="h-4 w-4 text-muted-foreground" />
                  <span>{person.religion}</span>
                </div>
              )}
              {person.notes && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-muted-foreground">{person.notes}</p>
                </div>
              )}
              {!person.gender &&
                !person.birthDate &&
                !person.birthPlace &&
                !person.deathDate &&
                !person.deathPlace &&
                !person.burialPlace &&
                !person.occupation &&
                !person.religion &&
                !person.notes && (
                  <p className="text-sm text-muted-foreground">Keine weiteren Details vorhanden.</p>
                )}
            </CardContent>
          </Card>

          {/* Relations */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Familie</CardTitle>
              <Dialog
                open={dialogOpen}
                onOpenChange={(open) => {
                  setDialogOpen(open);
                  if (!open) resetForm();
                }}
              >
                <DialogTrigger asChild>
                  <Button size="sm" disabled={availablePersons.length === 0}>
                    <Plus className="mr-2 h-4 w-4" />
                    Beziehung
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Beziehung hinzufugen</DialogTitle>
                    <DialogDescription>
                      Verknupfe {formatName(person)} mit einer anderen Person.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateRelation} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Person</Label>
                      <Select value={selectedPersonId} onValueChange={setSelectedPersonId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Person auswahlen" />
                        </SelectTrigger>
                        <SelectContent>
                          {availablePersons.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {formatName(p)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Beziehung</Label>
                      <Select
                        value={relationType}
                        onValueChange={(v) => setRelationType(v as RelationType)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Beziehungstyp" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PARENT">
                            {formatName(person)} ist Elternteil von...
                          </SelectItem>
                          <SelectItem value="CHILD">
                            {formatName(person)} ist Kind von...
                          </SelectItem>
                          <SelectItem value="SPOUSE">
                            {formatName(person)} ist Ehepartner von...
                          </SelectItem>
                          <SelectItem value="PARTNER">
                            {formatName(person)} ist Partner von...
                          </SelectItem>
                          <SelectItem value="SIBLING">
                            {formatName(person)} ist Geschwister von...
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {error && <p className="text-sm text-red-500">{error}</p>}

                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        Abbrechen
                      </Button>
                      <Button type="submit" disabled={creating}>
                        {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Hinzufugen
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {(!person.relations || person.relations.length === 0) ? (
                <p className="text-sm text-muted-foreground">
                  Noch keine Beziehungen vorhanden.
                  {availablePersons.length === 0 && ' Fuge zuerst weitere Personen hinzu.'}
                </p>
              ) : (
                <div className="space-y-4">
                  {/* Parents */}
                  {groupedRelations.PARENT && groupedRelations.PARENT.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Eltern
                      </h4>
                      <div className="space-y-2">
                        {groupedRelations.PARENT.map((rel) => (
                          <RelationCard
                            key={rel.id}
                            relation={rel}
                            projectId={projectId!}
                            onDelete={handleDeleteRelation}
                            deleting={deleting === rel.id}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Spouse/Partner */}
                  {((groupedRelations.SPOUSE && groupedRelations.SPOUSE.length > 0) ||
                    (groupedRelations.PARTNER && groupedRelations.PARTNER.length > 0)) && (
                    <div>
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Heart className="h-4 w-4" />
                        Partner
                      </h4>
                      <div className="space-y-2">
                        {groupedRelations.SPOUSE?.map((rel) => (
                          <RelationCard
                            key={rel.id}
                            relation={rel}
                            projectId={projectId!}
                            onDelete={handleDeleteRelation}
                            deleting={deleting === rel.id}
                            label="Ehepartner"
                          />
                        ))}
                        {groupedRelations.PARTNER?.map((rel) => (
                          <RelationCard
                            key={rel.id}
                            relation={rel}
                            projectId={projectId!}
                            onDelete={handleDeleteRelation}
                            deleting={deleting === rel.id}
                            label="Partner"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Siblings */}
                  {groupedRelations.SIBLING && groupedRelations.SIBLING.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Geschwister
                      </h4>
                      <div className="space-y-2">
                        {groupedRelations.SIBLING.map((rel) => (
                          <RelationCard
                            key={rel.id}
                            relation={rel}
                            projectId={projectId!}
                            onDelete={handleDeleteRelation}
                            deleting={deleting === rel.id}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Children */}
                  {groupedRelations.CHILD && groupedRelations.CHILD.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                        <Baby className="h-4 w-4" />
                        Kinder
                      </h4>
                      <div className="space-y-2">
                        {groupedRelations.CHILD.map((rel) => (
                          <RelationCard
                            key={rel.id}
                            relation={rel}
                            projectId={projectId!}
                            onDelete={handleDeleteRelation}
                            deleting={deleting === rel.id}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Documents */}
        {documents.length > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Dokumente ({documents.length})
              </CardTitle>
              <Link to={`/projects/${projectId}/documents`}>
                <Button variant="outline" size="sm">
                  Alle Dokumente
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {documents.map((doc) => (
                  <DocumentCard key={doc.id} document={doc} projectId={projectId!} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// Helper to get icon for file type
function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) {
    return <ImageIcon className="h-6 w-6 text-blue-500" />;
  }
  if (mimeType === 'application/pdf') {
    return <FileText className="h-6 w-6 text-red-500" />;
  }
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
    return <FileSpreadsheet className="h-6 w-6 text-green-500" />;
  }
  if (mimeType.includes('word') || mimeType.includes('document')) {
    return <FileText className="h-6 w-6 text-blue-600" />;
  }
  return <File className="h-6 w-6 text-gray-500" />;
}

interface DocumentCardProps {
  document: Document;
  projectId: string;
}

function DocumentCard({ document, projectId }: DocumentCardProps) {
  return (
    <Link
      to={`/projects/${projectId}/documents`}
      className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors"
    >
      {document.isImage ? (
        <img
          src={document.fileUrl}
          alt={document.title}
          className="w-12 h-12 object-cover rounded"
        />
      ) : (
        <div className="w-12 h-12 flex items-center justify-center bg-muted rounded">
          {getFileIcon(document.mimeType)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{document.title}</p>
        <p className="text-xs text-muted-foreground">
          {DOCUMENT_TYPE_LABELS[document.documentType]}
        </p>
        {document.documentDate && (
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
            <Calendar className="h-3 w-3" />
            {formatDateDE(document.documentDate)}
          </p>
        )}
      </div>
    </Link>
  );
}

interface RelationCardProps {
  relation: {
    id: string;
    type: RelationType;
    person: PersonSummary;
  };
  projectId: string;
  onDelete: (id: string) => void;
  deleting: boolean;
  label?: string;
}

function RelationCard({ relation, projectId, onDelete, deleting, label }: RelationCardProps) {
  function formatName(p: PersonSummary): string {
    const parts = [];
    if (p.firstName) parts.push(p.firstName);
    if (p.lastName) parts.push(p.lastName);
    if (parts.length === 0) return 'Unbekannt';
    return parts.join(' ');
  }

  return (
    <div className="flex items-center justify-between p-2 rounded-md bg-muted/50">
      <Link
        to={`/projects/${projectId}/persons/${relation.person.id}`}
        className="flex items-center gap-2 hover:text-primary"
      >
        <User className="h-4 w-4" />
        <span>{formatName(relation.person)}</span>
        {label && <span className="text-xs text-muted-foreground">({label})</span>}
      </Link>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-muted-foreground hover:text-destructive"
        onClick={() => onDelete(relation.id)}
        disabled={deleting}
      >
        {deleting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Trash2 className="h-4 w-4" />
        )}
      </Button>
    </div>
  );
}
